import { computed, inject, Signal } from '@angular/core';
import {
    patchState,
    signalStore,
    StateSignals,
    withComputed,
    withMethods,
    withState,
    WritableStateSource,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, catchError, of, pipe, switchMap, tap } from 'rxjs';
import {
    ChatItem,
    ChatChip,
} from '../chat-window/chat-window.component';
import { ChatResponse } from '../../models/chat.models';
import { LocalChatService } from '../../services/local-chat.service';
import { FaqAdapter } from '../../services/faq.adapter';
import {
    buildConversationContext,
    buildFollowUpChips,
    CATEGORY_REENTRY_MESSAGE_KEY,
    CategorySnapshot,
    classifyChip,
    CLOSE_CHIP_ID,
    createDisplayBubble,
    FailedRequest,
    hasRemainingChildren,
    mapOptionsToChips,
    RETRY_CHIP_ID,
    TYPING_ITEM,
    WidgetState,
} from './chat-conversation.logic';

/**
 * Single source of truth for the chip-driven chat widget.
 *
 * All conversation state lives here as signals, and every transition is a
 * method that writes via `patchState` (immutable updates). The component is a
 * thin projection: it binds these signals and forwards UI events. Pure
 * tree-walk decisions are delegated to `chat-conversation.logic.ts`.
 */
interface ChatState {
    widgetState: WidgetState;
    isLoading: boolean;
    initialized: boolean;
    items: ChatItem[];
    chips: ChatChip[];
    rootChips: ChatChip[];
    selectedRootNodeId: string;
    rootNodeTargetId: string;
    visitedNodeIds: string[];
    childNodeCount: number;
    excludedNodeCount: number;
    currentNodeId: string;
    lastFailedRequest: FailedRequest | null;
    categoryHistories: Record<string, CategorySnapshot>;
    lastVisitedNodeId: string;
}

const initialState: ChatState = {
    widgetState: 'closed',
    isLoading: false,
    initialized: false,
    items: [],
    chips: [],
    rootChips: [],
    selectedRootNodeId: '',
    rootNodeTargetId: '',
    visitedNodeIds: [],
    childNodeCount: 0,
    excludedNodeCount: 0,
    currentNodeId: '',
    lastFailedRequest: null,
    categoryHistories: {},
    lastVisitedNodeId: '',
};

type ConversationStore = StateSignals<ChatState> &
    WritableStateSource<ChatState> & {
        conversationContext: Signal<string | null>;
    };

function createRequestHelpers(
    store: ConversationStore,
    chat: LocalChatService,
    faqAdapter: FaqAdapter,
) {
    const getExcludedNodeIds = (): string[] => {
        const excluded: string[] = [];
        if (!faqAdapter.cashToCloseFinalIsFromBorrower()) {
            excluded.push('fundsdueatclosing');
        }
        if (faqAdapter.cashToCloseFinalIsFromBorrower()) {
            excluded.push('morethaniowe');
        }
        if (!faqAdapter.hasData()) {
            excluded.push('loaninfo');
        }
        if (faqAdapter.payoffs().length === 0) {
            excluded.push('payoffs');
        }
        if (!faqAdapter.hasNbthParticipants()) {
            excluded.push('nbthsign');
        }
        return excluded;
    };
    const resetItemsToStart = () => {
        patchState(store, { items: [] });
    };

    const beginRequest = () => {
        patchState(store, {
            isLoading: true,
            items: [...store.items(), TYPING_ITEM],
        });
    };

    const removeTypingItem = () => {
        patchState(store, {
            items: store.items().filter((item: ChatItem) => item.kind !== 'typing'),
        });
    };

    const removeRetryChip = () => {
        patchState(store, {
            chips: store.chips().filter((c: ChatChip) => c.id !== RETRY_CHIP_ID),
        });
    };

    const handleStartResponse = (response: ChatResponse) => {
        removeTypingItem();
        patchState(store, {
            items: [...store.items(), createDisplayBubble('bot', 'help-faq.prompts.START')],
            currentNodeId: response.currentNodeId,
            chips: [],
            rootChips: mapOptionsToChips(
                response.options,
                response.currentNodeId,
                store.visitedNodeIds(),
                getExcludedNodeIds()
            ),
            lastFailedRequest: null,
            isLoading: false,
        });
    };

    const handleResponse = (response: ChatResponse, messageKeyOverride?: string) => {
        removeTypingItem();
        const categoryPrompts = new Set(['signing', 'loan']);
        const bubble = messageKeyOverride
            ? createDisplayBubble('bot', messageKeyOverride)
            : (categoryPrompts.has(response.currentNodeId)
                ? createDisplayBubble('bot', `help-faq.prompts.${response.currentNodeId}`)
                : { kind: 'bot' as const, text: response.currentNodeId });
        patchState(store, {
            items: [...store.items(), bubble],
            currentNodeId: response.currentNodeId,
            chips: mapOptionsToChips(
                response.options,
                response.currentNodeId,
                store.visitedNodeIds(),
                getExcludedNodeIds()
            ),
            lastFailedRequest: null,
            isLoading: false,
        });
    };

    const handleError = (failed: FailedRequest) => {
        removeTypingItem();
        patchState(store, {
            items: [...store.items(), createDisplayBubble('bot', 'help-faq.chat.ERROR')],
            chips: [{ id: RETRY_CHIP_ID, label: 'help-faq.chat.TRY_AGAIN' }],
            lastFailedRequest: failed,
            isLoading: false,
        });
    };

    const applySelectFollowUp = (chip: ChatChip, response: ChatResponse) => {
        if (chip.parentNodeId === 'root' && response.isDynamic) {
            patchState(store, { chips: [] });
        }

        if (chip.parentNodeId === 'root') {
            const excludedIds = getExcludedNodeIds();
            patchState(store, {
                childNodeCount: response.options.length,
                excludedNodeCount: excludedIds.filter(id =>
                    response.options.some(o => o.targetNodeId === id)
                ).length,
            });
        } else if (hasRemainingChildren(store.childNodeCount(), store.visitedNodeIds().length, store.excludedNodeCount())) {
            patchState(store, {
                items: [...store.items(), createDisplayBubble('bot', 'help-faq.prompts.FOLLOW_UP')],
                chips: chip.parentNodeId ? buildFollowUpChips(chip.parentNodeId) : [],
            });
        } else if (chip.parentNodeId !== undefined) {
            patchState(store, {
                items: [
                    ...store.items(),
                    createDisplayBubble('bot', 'help-faq.prompts.COMPLETION', {
                        category: `help-faq.categories.${store.rootNodeTargetId()}`,
                    }),
                ],
                chips: [],
            });
        }
    };

    const loadStartNode = rxMethod<void>(
        pipe(
            tap(() => beginRequest()),
            switchMap(() =>
                chat.getStartNode().pipe(
                    tap((response) => handleStartResponse(response)),
                    catchError(() => {
                        handleError({ kind: 'start' });
                        return of(null);
                    })
                )
            )
        )
    );

    const dispatchSelect = rxMethod<{ chip: ChatChip; overrideMessage: boolean }>(
        pipe(
            tap(() => {
                patchState(store, { chips: [] });
                beginRequest();
            }),
            switchMap(({ chip, overrideMessage }) => {
                const { targetNodeId } = chip;
                if (!targetNodeId) return EMPTY;
                const context = buildConversationContext(store.items());
                return chat
                    .selectOption({ nodeId: targetNodeId, conversationContext: context })
                    .pipe(
                        tap((response) => {
                            handleResponse(
                                response,
                                overrideMessage ? CATEGORY_REENTRY_MESSAGE_KEY : undefined
                            );
                            applySelectFollowUp(chip, response);
                        }),
                        catchError(() => {
                            handleError({ kind: 'select', chip });
                            return of(null);
                        })
                    );
            })
        )
    );

    const retrySelect = rxMethod<{ kind: 'select'; chip: ChatChip }>(
        pipe(
            tap(() => beginRequest()),
            switchMap((failed) => {
                const { targetNodeId } = failed.chip;
                if (!targetNodeId) return EMPTY;
                const context = buildConversationContext(store.items());
                return chat
                    .selectOption({ nodeId: targetNodeId, conversationContext: context })
                    .pipe(
                        tap((response) => handleResponse(response)),
                        catchError(() => {
                            handleError(failed);
                            return of(null);
                        })
                    );
            })
        )
    );

    const retry = () => {
        const failed = store.lastFailedRequest();
        if (!failed) return;
        removeRetryChip();
        if (failed.kind === 'start') {
            loadStartNode();
        } else {
            retrySelect(failed);
        }
    };

    return {
        resetItemsToStart,
        loadStartNode,
        dispatchSelect,
        retry,
    };
}

type RequestHelpers = ReturnType<typeof createRequestHelpers>;

function createChipHandlers(store: ConversationStore, helpers: RequestHelpers) {
    const { resetItemsToStart, loadStartNode, dispatchSelect } = helpers;

    const enterCategory = (chip: ChatChip) => {
        const { targetNodeId } = chip;
        if (!targetNodeId || store.rootNodeTargetId() === targetNodeId) return;
        patchState(store, { rootNodeTargetId: targetNodeId, visitedNodeIds: [], excludedNodeCount: 0 });
        resetItemsToStart();
    };

    const snapshotCurrentCategory = () => {
        const id = store.rootNodeTargetId();
        if (!id) return;
        patchState(store, {
            categoryHistories: {
                ...store.categoryHistories(),
                [id]: {
                    items: store.items(),
                    chips: store.chips(),
                    visitedNodeIds: store.visitedNodeIds(),
                    childNodeCount: store.childNodeCount(),
                    excludedNodeCount: store.excludedNodeCount(),
                    currentNodeId: store.currentNodeId(),
                },
            },
        });
    };

    const handleRootReset = () => {
        patchState(store, { selectedRootNodeId: '', visitedNodeIds: [], excludedNodeCount: 0 });
        resetItemsToStart();
        loadStartNode();
    };

    const handleCategoryChange = (chip: ChatChip) => {
        const { targetNodeId } = chip;
        if (!targetNodeId) return;
        snapshotCurrentCategory();

        const existing = store.categoryHistories()[targetNodeId];
        if (existing) {
            patchState(store, {
                rootNodeTargetId: targetNodeId,
                selectedRootNodeId: chip.id,
                items: existing.items,
                chips: existing.chips,
                visitedNodeIds: existing.visitedNodeIds,
                childNodeCount: existing.childNodeCount,
                excludedNodeCount: existing.excludedNodeCount,
                currentNodeId: existing.currentNodeId,
            });
            return;
        }

        enterCategory(chip);
        patchState(store, { selectedRootNodeId: chip.id, visitedNodeIds: [], excludedNodeCount: 0 });
        resetItemsToStart();
        dispatchSelect({ chip, overrideMessage: false });
    };

    const handleStaticYes = (chip: ChatChip) => {
        enterCategory(chip);
        patchState(store, {
            items: [...store.items(), createDisplayBubble('user', chip.label)],
        });
        dispatchSelect({ chip, overrideMessage: true });
    };

    const handleStaticNo = () => {
        patchState(store, {
            chips: [],
            items: [
                ...store.items(),
                createDisplayBubble('user', 'help-faq.chat.NO'),
                createDisplayBubble('bot', 'help-faq.chat.THANK_YOU'),
            ],
        });
        patchState(store, {
            chips: [{ id: CLOSE_CHIP_ID, label: 'help-faq.chat.CLOSE_CHAT' }],
        });
    };

    const handleLeafSelection = (chip: ChatChip) => {
        const { targetNodeId } = chip;
        if (!targetNodeId) return;
        patchState(store, {
            visitedNodeIds: [...store.visitedNodeIds(), targetNodeId],
            lastVisitedNodeId: targetNodeId,
            items: [...store.items(), createDisplayBubble('user', chip.label)],
        });
        dispatchSelect({ chip, overrideMessage: false });
    };

    return {
        handleRootReset,
        handleCategoryChange,
        handleStaticYes,
        handleStaticNo,
        handleLeafSelection,
    };
}

export const ChatConversationStore = signalStore(
    withState(initialState),
    withComputed(({ items }) => ({
        conversationContext: computed(() => buildConversationContext(items())),
    })),
    withMethods((
        store,
        chat = inject(LocalChatService),
        faqAdapter = inject(FaqAdapter),
    ) => {
        const requestHelpers = createRequestHelpers(store, chat, faqAdapter);
        const { loadStartNode, retry } = requestHelpers;
        const {
            handleRootReset,
            handleCategoryChange,
            handleStaticYes,
            handleStaticNo,
            handleLeafSelection,
        } = createChipHandlers(store, requestHelpers);

        return {
            open() {
                patchState(store, { widgetState: 'opened' });
                if (store.initialized()) return;

                patchState(store, { items: [], chips: [], initialized: true });
                loadStartNode();
            },

            minimize() {
                patchState(store, { widgetState: 'minimized' });
            },

            close() {
                patchState(store, initialState);
            },

            selectChip(chip: ChatChip): void {
                if (store.isLoading()) return;

                if (chip.id === RETRY_CHIP_ID) return retry();
                if (chip.id === CLOSE_CHIP_ID) {
                    patchState(store, initialState);
                    return;
                }

                if (!chip.targetNodeId) return;

                const intent = classifyChip(chip, store.rootNodeTargetId());
                switch (intent) {
                    case 'root-reset':
                        return handleRootReset();
                    case 'category-change':
                        return handleCategoryChange(chip);
                    case 'static-yes':
                        return handleStaticYes(chip);
                    case 'static-no':
                        return handleStaticNo();
                    case 'static-close':
                        return;
                    case 'leaf-selection':
                        return handleLeafSelection(chip);
                    default: {
                        const exhaustive: never = intent;
                        throw new Error(`Unhandled ChipIntent: ${exhaustive}`);
                    }
                }
            },
        };
    })
);
